import {
	App,
	AppError,
	AppMenuPoint,
	AppMenuPoints,
	AppNavMenuPointsService,
	ErrorService,
	ErrorType,
	getColor,
	getEnvironmentFromUrl,
	getWidgetFontFamily,
	getWidgetTextColor,
	ManageRoutes,
	MenuPointService,
	NavVariant,
	Role,
} from '@agilox/common';
import {
	CookieOverlayComponent,
	ErrorContentComponent,
	ErrorPageComponent,
	NavModule,
	ToastContainerComponent,
} from '@agilox/components';
import { OverlayComponent } from '@agilox/ui';
import { MenuPoint, MenuPointCallbackAction } from '@agilox/ui-common';
import { LowerCasePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterModule } from '@angular/router';
import { AuthService } from '@auth';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UserStoreService } from '@user-store';
import { Chart, registerables } from 'chart.js';
import { filter, map, Observable, shareReplay, startWith } from 'rxjs';
import { HelpContentComponent } from './general/shared/components/help-content/help-content.component';

@Component({
	selector: 'manage-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	imports: [
		RouterModule,
		TranslatePipe,
		LowerCasePipe,
		ToastContainerComponent,
		CookieOverlayComponent,
		OverlayComponent,
		HelpContentComponent,
		ErrorContentComponent,
		ErrorPageComponent,
		NavModule,
	],
})
export class AppComponent implements OnInit {
	protected errorService = inject(ErrorService);
	private menuPointService = inject(MenuPointService);
	public userStore = inject(UserStoreService);
	public auth = inject(AuthService);
	private translateService = inject(TranslateService);
	private appMenuPointsService = inject(AppNavMenuPointsService);
	private router = inject(Router);
	private destroyRef = inject(DestroyRef);

	private role = computed(() => this.userStore.currentUser()?.role);

	private roleAndLanguage = computed(() => {
		return {
			role: this.role(),
			language: this.language(),
		};
	});

	public desktopMenuPoints = computed(() => {
		return this.menuPointService.getMenuPointsByCategory(
			NavVariant.DESKTOP,
			this.roleAndLanguage().role as Role
		);
	});

	public mobileMenuPoints = computed(() => {
		return this.menuPointService.getMenuPointsByCategory(
			NavVariant.MOBILE,
			this.roleAndLanguage().role as Role
		);
	});

	public appNavMenuPoints = computed(() => {
		return this.appMenuPointsService.getAppNavMenuPoints(this.roleAndLanguage().role as Role);
	});

	private _routerEvents$: Observable<any> = this.router.events.pipe(
		takeUntilDestroyed(),
		shareReplay(1)
	);

	activeApp: AppMenuPoint | undefined = AppMenuPoints.find((app) => app.name === App.manage);
	environment: string = getEnvironmentFromUrl(ManageRoutes);
	error$: Observable<AppError | null> = this.errorService.error$;
	helpOpen = signal(false);

	/**
	 * Will get triggered when a navigation chunk fails to load because the user is offline
	 * or the chunk is not available (e.g. due to a new version of the app)
	 */
	private _navigationError$: Observable<NavigationError> = this._routerEvents$.pipe(
		takeUntilDestroyed(this.destroyRef),
		filter((event) => event instanceof NavigationError)
	);

	private pageName$ = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationEnd),
		map((event) => {
			return 'p-' + event.urlAfterRedirects.replace('/', '').replace(/\//g, '-').split('?')[0];
		}),
		takeUntilDestroyed(this.destroyRef)
	);

	private currentUrl$ = this._routerEvents$.pipe(
		filter((event) => event instanceof NavigationEnd),
		map((event) => event.urlAfterRedirects),
		takeUntilDestroyed(this.destroyRef)
	);

	private language = toSignal(
		this.translateService.onLangChange.pipe(
			startWith({ lang: this.translateService.currentLang || 'en' })
		)
	);

	pageName = toSignal(this.pageName$);
	currentUrl = toSignal(this.currentUrl$);
	error = toSignal(this.error$);

	constructor() {
		this._navigationError$.subscribe((event) => {
			if (!navigator.onLine) {
				this.errorService.setError({
					application: App.manage,
					type: ErrorType.NO_INTERNET_ERROR,
				});
			} else if (event.error.toString().includes('chunk')) {
				this.errorService.setError({
					application: App.manage,
					type: ErrorType.CHUNK_ERROR,
				});
			}
		});
	}

	ngOnInit() {
		const lang = this.userStore.currentUser()?.lang || 'en';
		this.translateService.use(lang);
		this.setupChartJs();
	}

	setupChartJs() {
		Chart.register(...registerables);
		Chart.defaults.color = getWidgetTextColor();
		Chart.defaults.font.family = getWidgetFontFamily();
		Chart.defaults.plugins.tooltip.backgroundColor = getColor('--color-secondary-900');
		Chart.defaults.plugins.tooltip.titleColor = getColor('--color-white');
		Chart.defaults.plugins.tooltip.bodyColor = getColor('--color-white');
		Chart.defaults.plugins.tooltip.footerColor = getColor('--color-white');
	}

	navCallback(menuPoint: MenuPoint) {
		if (menuPoint.callbackAction === MenuPointCallbackAction.HELP) {
			this.helpOpen.set(true);
			return;
		}

		if (menuPoint.callbackAction === MenuPointCallbackAction.LOGOUT) {
			this.logout();
		}
	}

	onClose() {
		this.errorService.clearError();
	}

	onCloseHelp() {
		this.helpOpen.set(false);
	}

	logout() {
		this.auth.logout();
	}
}
